import * as React from "react";
import styled, { css } from "styled-components";

import { withDeposit } from "../common/withDeposit";
import { fontSize } from "../common/styled-mixins";
import Pagination from "../Pagination";
import _Spinner from "../Spinners/SimpleSpinner";
import NoData from "./NoData";
import { Flex } from "./shared-styles";

const Container = styled(Flex)`
  margin-top: 34px;
  margin-bottom: 30px;

  ${props =>
    props.showDeposit &&
    css`
      :before {
        content: "";
        position: absolute;
        top: -34px;
        left: 50%;
        transform: translateX(-50%);
        width: 880px;
        border-top: 1px solid ${({ theme }) => theme.tableBefore || "#cccccc"};

        @media (max-width: 959px) {
          width: 100%;
          left: 0;
          transform: translateX(0);
        }
      }
    `};

  ${props =>
    props.loading &&
    css`
      :after {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        background: hsla(0, 0%, 100%, 0.8);
        transition: 0.3s;
      }
    `}
`;

const Spinner = styled(_Spinner)`
  position: absolute;
  font-size: 26px;
  z-index: 1;
`;

const TableWrapper = styled.div`
  width: 100%;
  ${props =>
    props.type === "widget" &&
    css`
      max-height: 400px;
      overflow-x: hidden;

      table th {
        position: sticky;
        top: 0;
        z-index: 2;
        background-color: ${({ theme }) =>
          theme.tableRowOdd ? theme.mainHighlight : "#f5f5f5"};
      }
    `}
`;

const Table = styled.table`
  width: 100%;
`;

const THead = styled.thead`
  font-weight: 400;
  font-size: ${fontSize(12)};
  text-transform: uppercase;
  color: #a5a4a4;
  box-shadow: none;

  @media (max-width: 640px) {
    font-size: ${fontSize(10)};
  }

  @media (max-width: 414px) {
    font-size: ${fontSize(12)};
  }

  @media (max-width: 360px) {
    font-size: ${fontSize(10)};
  }
`;

const TableHeadRow = styled.th`
  padding: 5px;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #fff;

  &:nth-child(odd) {
    background-color: ${({ theme }) =>
      theme.tableRowOdd ? theme.mainHighlight : "#f6f6f6"};
    border-bottom: 1px solid
      ${({ theme }) => theme.tableCellBorder || "#ffffff"};
  }
  &:nth-child(even) {
    background-color: ${({ theme }) =>
      theme.tableRowEven ? theme.mainDark : "#efefef"};
    border-bottom: 1px solid
      ${({ theme }) => theme.tableCellBorder || "#ffffff"};
  }
  &:hover {
    background-color: rgba(255, 255, 255, 0.25);
  }
  ${props =>
    props.clickable &&
    css`
      cursor: pointer;
    `}
`;

const TableCol = styled.td`
  min-height: 4.429em;
  font-size: ${fontSize(14)};
  line-height: 1;
  text-align: center;
  color: ${({ theme }) => theme.tableCellColor || "#717171"};
  padding: 15px 5px;
`;

const PaginationWrapper = styled.div`
  width: 100%;
  padding: 20px 0;
`;

type Props = {
  startDate: moment$Moment,
  endDate: moment$Moment
};

function HistoryTable(props: Props) {
  const {
    showDeposit,
    columns,
    errorText,
    startDate,
    endDate,
    data,
    onClick,
    loading,
    ...rest
  } = props;

  const dataWithShortCryptoIndex = data.map(item => {
    if (item.stock && item.stock.toLowerCase() === "cryptoindex") {
      item.stock = "CRYPTO";
    }
    return item;
  });

  const makeColumns = row => {
    return columns.map(
      (column, i) =>
        column.show !== false && (
          <TableCol key={i} style={column.style}>
            {column.Cell ? column.Cell({ row }) : row[column.accessor]}
          </TableCol>
        )
    );
  };

  const TableHead = () => {
    return (
      <THead>
        <tr>
          {columns.map(
            (column, i) =>
              column.show !== false && (
                <TableHeadRow key={i} show={column.show}>
                  {column.Header}
                </TableHeadRow>
              )
          )}
        </tr>
      </THead>
    );
  };

  const TableBody = () => {
    return (
      <tbody>
        {dataWithShortCryptoIndex.map((element, index) => (
          <TableRow
            clickable={onClick ? true : false}
            key={index}
            onClick={onClick ? () => onClick(element) : () => false}
          >
            {makeColumns(element)}
          </TableRow>
        ))}
      </tbody>
    );
  };

  return (
    <Container showDeposit={showDeposit} loading={loading}>
      {loading && <Spinner accent />}
      {data.length === 0 ? (
        <NoData
          loading={loading}
          error={errorText}
          startDate={startDate}
          endDate={endDate}
        />
      ) : (
        <React.Fragment>
          <TableWrapper type={rest.type}>
            <Table>
              <TableHead />
              <TableBody />
            </Table>
          </TableWrapper>
          <PaginationWrapper>
            <Pagination
              page={rest.page}
              pages={rest.pages}
              pageSize={rest.pageSize}
              onPageChange={rest.onPageChange}
              onPageSizeChange={rest.onPageSizeChange}
            />
          </PaginationWrapper>
        </React.Fragment>
      )}
    </Container>
  );
}

export default withDeposit(HistoryTable);



// WEBPACK FOOTER //
// ./src/components/HistoryPages/HistoryTable.js